/**
 * src/lib/voice/sentenceBuffer.js — Streaming text → sentence chunker
 *
 * Buffers incoming text tokens from an SSE stream and emits complete sentences
 * for TTS processing. Sentences are emitted when a terminator (. ! ? \n\n) is
 * detected after a minimum character count, preventing splitting on abbreviations.
 *
 * Usage:
 *   const buf = createSentenceBuffer({
 *     onSentence: (text) => speak(text),
 *   });
 *   buf.push(token);  // call for each SSE text token
 *   buf.flush();      // call when stream ends
 */

const TERMINATORS = /[.!?](\s|$)|\n\n/;
const MIN_SENTENCE_LEN = 15;  // avoid splitting "OK." or "Dr. Smith"
const MAX_SENTENCE_LEN = 300; // force flush for very long sentences

// PHASE 2B (first-audio latency) — the FIRST chunk of a turn may cut on a
// CLAUSE boundary (comma / semicolon / colon) in addition to the sentence
// terminators, with a lower minimum length (~6-8 words). This lets the agent
// start speaking on the first clause ("Sure,", "Of course,", "Based on …,")
// instead of waiting for the first full sentence — shaving the ~first-sentence
// gap down to ~first-clause. After chunk #0 the buffer reverts to full-sentence
// granularity (TERMINATORS + MIN_SENTENCE_LEN) so prosody stays natural and we
// don't pay extra per-clause TTS cost mid-reply.
const FIRST_CHUNK_TERMINATORS = /[.!?,;:](\s|$)|\n\n/;
const FIRST_CHUNK_MIN_LEN = 25; // ~6-8 words before a clause cut is allowed

/**
 * Creates a sentence buffer that calls onSentence for each complete sentence.
 * @param {{ onSentence: (text: string) => void, firstChunkClause?: boolean }} options
 *   firstChunkClause — when true, the FIRST emitted chunk may cut on a clause
 *   boundary (comma/semicolon/colon) at a lower length floor; subsequent chunks
 *   use the default full-sentence granularity.
 */
export function createSentenceBuffer({ onSentence, firstChunkClause = false }) {
  let buffer = '';
  // Per-buffer flag — true until the first chunk is emitted, then false. This
  // is intentionally per-instance (a fresh buffer is created per turn) so the
  // clause-cut only ever affects the opening chunk of each turn.
  let firstChunk = firstChunkClause;

  function tryEmit() {
    // Choose the cut rule for the chunk currently being emitted. While firstChunk
    // is still armed, use the clause-aware terminators + lower floor; otherwise
    // the established full-sentence rule.
    //
    // TICKET-CODEX-3368118025 — when firstChunk is armed, only apply the raised
    // FIRST_CHUNK_MIN_LEN floor for clause terminators (comma/semicolon/colon).
    // Sentence-ending terminators (.!?) keep the standard MIN_SENTENCE_LEN floor
    // so short complete first sentences like "Let me check that." (15–24 chars)
    // still emit immediately instead of stalling until more text arrives.
    while (true) {
      const terminators = firstChunk ? FIRST_CHUNK_TERMINATORS : TERMINATORS;
      // For the first chunk we use MIN_SENTENCE_LEN as the floor first: if a
      // sentence terminator (.!?) is found past that shorter floor we emit right
      // away; only when no sentence terminator exists do we try the higher
      // FIRST_CHUNK_MIN_LEN floor for a clause cut.
      const sentenceMatch = firstChunk
        ? TERMINATORS.exec(buffer.slice(MIN_SENTENCE_LEN - 1))
        : null;
      const minLen = (firstChunk && !sentenceMatch) ? FIRST_CHUNK_MIN_LEN : MIN_SENTENCE_LEN;
      if (buffer.length < minLen) break;

      const match = terminators.exec(buffer.slice(minLen - 1));
      if (!match) {
        // No terminator found — check if buffer exceeds max (force flush at word boundary)
        if (buffer.length >= MAX_SENTENCE_LEN) {
          const spaceIdx = buffer.lastIndexOf(' ', MAX_SENTENCE_LEN);
          const cutIdx = spaceIdx > minLen ? spaceIdx : MAX_SENTENCE_LEN;
          const sentence = buffer.slice(0, cutIdx).trim();
          buffer = buffer.slice(cutIdx).trimStart();
          if (sentence) { onSentence(sentence); firstChunk = false; }
        } else {
          break;
        }
        continue;
      }

      // Cut at the terminator position (relative to full buffer)
      const terminatorIdx = (minLen - 1) + match.index + match[0].length;
      const sentence = buffer.slice(0, terminatorIdx).trim();
      buffer = buffer.slice(terminatorIdx).trimStart();
      if (sentence) { onSentence(sentence); firstChunk = false; }
    }
  }

  return {
    /** Push one or more text tokens from the SSE stream */
    push(text) {
      if (!text) return;
      buffer += text;
      tryEmit();
    },
    /** Flush remaining buffer at end of stream */
    flush() {
      const remaining = buffer.trim();
      buffer = '';
      if (remaining) { onSentence(remaining); firstChunk = false; }
    },
    /** Reset without emitting (e.g., on cancel) */
    reset() {
      buffer = '';
      firstChunk = firstChunkClause;
    },
  };
}
